import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { SLIDE_TYPE_MODULE } from "../constants/webPermissions";

export function useWebPermissions() {
  const auth = useSelector((state) => state.auth || {});
  const role = String(auth.role || "").toLowerCase();
  const permissions = auth.web_permissions || {};

  const isPlatformAdmin = role === "admin";
  const isCustomUser = role === "custom_user";

  const can = useCallback(
    (moduleKey, actionKey) => {
      if (isPlatformAdmin) return true;
      if (!isCustomUser || !moduleKey || !actionKey) return false;
      return Boolean(permissions?.[moduleKey]?.[actionKey]);
    },
    [isPlatformAdmin, isCustomUser, permissions]
  );

  const canAccessModule = useCallback(
    (moduleKey) => can(moduleKey, "access"),
    [can]
  );

  const canForSlideType = useCallback(
    (slideType, actionKey) => {
      const moduleKey = SLIDE_TYPE_MODULE[slideType];
      if (!moduleKey) return isPlatformAdmin;
      return can(moduleKey, actionKey);
    },
    [can, isPlatformAdmin]
  );

  const canEditContentModule = useCallback(
    (moduleKey) =>
      can(moduleKey, "slides_edit") ||
      can(moduleKey, "slides_add") ||
      can(moduleKey, "edit"),
    [can]
  );

  return useMemo(
    () => ({
      role,
      permissions,
      isPlatformAdmin,
      isCustomUser,
      can,
      canAccessModule,
      canForSlideType,
      canEditContentModule,
    }),
    [
      role,
      permissions,
      isPlatformAdmin,
      isCustomUser,
      can,
      canAccessModule,
      canForSlideType,
      canEditContentModule,
    ]
  );
}
